import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Edit, Trash2, Loader2, Mail, MessageCircle,
  Copy, Eye, Clock, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

const TemplatesPanel = () => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    template_type: 'email',
    category: 'quote_received',
    subject: '',
    body: '',
    auto_send: false,
    send_delay_minutes: 0
  });

  const categories = [
    { value: 'quote_received', label: 'Quote Received' },
    { value: 'booking_confirmed', label: 'Booking Confirmed' },
    { value: 'driver_en_route', label: 'Driver En Route' },
    { value: 'delivery_complete', label: 'Delivery Complete' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'support', label: 'Support' }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/admin/templates');
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      template_type: 'email',
      category: 'quote_received',
      subject: '',
      body: '',
      auto_send: false,
      send_delay_minutes: 0
    });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type,
      category: template.category,
      subject: template.subject || '',
      body: template.body,
      auto_send: template.auto_send || false,
      send_delay_minutes: template.send_delay_minutes || 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.body) {
      toast.error('Name and body are required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/api/admin/templates/${editingTemplate.template_id}`, formData);
        toast.success('Template updated');
      } else {
        await api.post('/api/admin/templates', formData);
        toast.success('Template created');
      }
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (template.is_default) {
      toast.error('Cannot delete default templates');
      return;
    }
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/api/admin/templates/${template.template_id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleCopy = (template) => {
    navigator.clipboard.writeText(template.body);
    toast.success('Template copied to clipboard');
  };

  const filteredTemplates = templates.filter(t => {
    if (activeTab === 'all') return true;
    return t.template_type === activeTab;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">Message Templates</h2>
          <p className="text-gray-500">Manage email and WhatsApp response templates</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Templates</TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="whatsapp">
            <MessageCircle className="h-4 w-4 mr-2" />
            WhatsApp
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No templates found. Create your first template!</p>
                <Button onClick={handleCreate} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredTemplates.map((template) => (
                <Card key={template.template_id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {template.template_type === 'email' ? (
                          <Mail className="h-5 w-5 text-blue-600" />
                        ) : (
                          <MessageCircle className="h-5 w-5 text-green-600" />
                        )}
                        <div>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <CardDescription className="capitalize">
                            {template.category.replace(/_/g, ' ')}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {template.is_default && (
                          <Badge variant="secondary">Default</Badge>
                        )}
                        {template.auto_send && (
                          <Badge className="bg-green-100 text-green-800">Auto</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {template.subject && (
                      <div className="text-sm">
                        <span className="text-gray-500">Subject: </span>
                        <span className="font-medium">{template.subject}</span>
                      </div>
                    )}
                    
                    <div className="bg-gray-50 p-3 rounded text-sm max-h-32 overflow-hidden">
                      <p className="whitespace-pre-wrap line-clamp-4">{template.body}</p>
                    </div>

                    {template.auto_send && template.send_delay_minutes > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span>Sends after {template.send_delay_minutes} min</span>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2 border-t">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setPreviewTemplate(template)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleCopy(template)}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(template)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      {!template.is_default && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(template)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Welcome Email"
                />
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={formData.template_type}
                  onValueChange={(v) => setFormData(f => ({ ...f, template_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={formData.category}
                onValueChange={(v) => setFormData(f => ({ ...f, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.template_type === 'email' && (
              <div className="space-y-2">
                <Label>Email Subject</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(f => ({ ...f, subject: e.target.value }))}
                  placeholder="e.g., Your Rocket Van Express Quote - {quote_id}"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Message Body *</Label>
              <Textarea
                value={formData.body}
                onChange={(e) => setFormData(f => ({ ...f, body: e.target.value }))}
                placeholder="Hi {customer_name},&#10;&#10;Your message here..."
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Available variables: {'{customer_name}'}, {'{quote_id}'}, {'{booking_ref}'}, {'{pickup}'}, {'{dropoff}'}, {'{datetime}'}, {'{eta}'}, {'{driver_name}'}
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="auto-send">Auto-send this template</Label>
                <p className="text-xs text-gray-500">
                  Automatically send when the trigger event occurs
                </p>
              </div>
              <Switch
                id="auto-send"
                checked={formData.auto_send}
                onCheckedChange={(v) => setFormData(f => ({ ...f, auto_send: v }))}
              />
            </div>

            {formData.auto_send && (
              <div className="space-y-2">
                <Label>Send Delay (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.send_delay_minutes}
                  onChange={(e) => setFormData(f => ({ ...f, send_delay_minutes: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500">
                  Set to 0 for immediate sending, or add a delay
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {editingTemplate ? 'Update' : 'Create'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewTemplate?.template_type === 'email' ? (
                <Mail className="h-5 w-5 text-blue-600" />
              ) : (
                <MessageCircle className="h-5 w-5 text-green-600" />
              )}
              {previewTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          {previewTemplate && (
            <div className="space-y-4">
              {previewTemplate.subject && (
                <div className="border-b pb-3">
                  <p className="text-sm text-gray-500">Subject</p>
                  <p className="font-medium">{previewTemplate.subject}</p>
                </div>
              )}
              
              <div className={`p-4 rounded-lg ${
                previewTemplate.template_type === 'whatsapp' 
                  ? 'bg-green-50 border border-green-100' 
                  : 'bg-gray-50'
              }`}>
                <pre className="whitespace-pre-wrap text-sm font-sans">
                  {previewTemplate.body}
                </pre>
              </div>

              {previewTemplate.variables && previewTemplate.variables.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">Template Variables</p>
                  <div className="space-y-1">
                    {previewTemplate.variables.map((v, i) => (
                      <div key={i} className="text-sm">
                        <code className="bg-gray-100 px-1 rounded">{`{${v.key}}`}</code>
                        <span className="text-gray-500 ml-2">- {v.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPanel;
